/**
 * Colour contrast, the WCAG 2.x way — pure, so the API's refusal, the admin
 * form's warning and the app's choice of ink agree to the digit.
 *
 * An edition picks its own accent (`accentColor` / `accent2Color`), and the
 * accent is not decoration: it is the colour of every link, of the set times
 * and of the text on a selected pill. A pale yellow accent under dark ink, or
 * a navy one on the near-black ground, is a lineup nobody can read in a field
 * at noon. So the API refuses an accent that does not reach AA on the ground,
 * and the ink on top of it is chosen per accent rather than fixed.
 */

/** The app's ground (`--bg` in `frontend/src/ui/tokens.css`), which links and accent text sit on. */
export const GROUND = '#08080f';
/** The two inks that can sit ON an accent: the ground's near-black, and `--txt`. */
export const DARK_INK = '#08080f';
export const LIGHT_INK = '#f3f1ea';
/** WCAG AA for body text. */
export const AA = 4.5;

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#rgb` / `#rrggbb` → [r, g, b] in 0–255, or null for anything else. */
export function parseHex(hex: string): [number, number, number] | null {
  if (!HEX.test(hex)) return null;
  const h = hex.length === 4 ? hex.slice(1).split('').map((c) => c + c).join('') : hex.slice(1);
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) throw new Error(`not a colour: ${hex}`);
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
}

/** The contrast ratio of two colours, 1 to 21, order-independent. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Rounded DOWN to one decimal, so a displayed "4.5:1" is never a 4.46 that failed. */
export function formatRatio(ratio: number): string {
  return `${(Math.floor(ratio * 10) / 10).toFixed(1)}:1`;
}

/**
 * The ink for text ON `accent`: dark if it reaches AA, else light if that
 * does, else whichever contrasts more (an accent that passes neither — a
 * mid-grey — was refused by the API, so this is only a fallback).
 */
export function inkOn(accent: string): string {
  const dark = contrastRatio(accent, DARK_INK), light = contrastRatio(accent, LIGHT_INK);
  if (dark >= AA) return DARK_INK;
  if (light >= AA) return LIGHT_INK;
  return dark >= light ? DARK_INK : LIGHT_INK;
}

export interface AccentCheck {
  /** Contrast of the accent against the ground, where it is text. */
  onGround: number;
  /** The ink `inkOn` picks, and its contrast against the accent. */
  ink: string;
  onAccent: number;
  ok: boolean;
}

/** Everything the admin form shows and the API decides, in one call. */
export function checkAccent(accent: string, ground: string = GROUND): AccentCheck {
  const onGround = contrastRatio(accent, ground);
  const ink = inkOn(accent);
  const onAccent = contrastRatio(accent, ink);
  return { onGround, ink, onAccent, ok: onGround >= AA && onAccent >= AA };
}

/**
 * The sentence a refusal carries, shared by the API and the admin form; null
 * when the colour is fine (or is not a colour — the format check says that).
 *
 * Only the GROUND can refuse: the dark ink is the ground's colour, so an
 * accent that reads on the ground always carries dark ink too. The light ink
 * is for accents stored before this rule, which nothing re-checked.
 */
export function accentRefusal(accent: string): string | null {
  if (!parseHex(accent)) return null;
  const onGround = contrastRatio(accent, GROUND);
  if (onGround >= AA) return null;
  return `Too dark to read on the app's background: ${formatRatio(onGround)}, and text needs at least 4.5:1. Choose a lighter colour.`;
}
